import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import { getGlAccountsClosing, createAccountsClosing } from '../../../../redux/actions/SocietyPortal/AccountsClosingAction/AccountsClosingAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { toast } from 'react-toastify';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const Closing = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { glAccountsData, glLoading, createLoading } = useSelector(state => state.AccountsClosingReducer);

    const [financialYear, setFinancialYear] = useState(getCurrentFY());

    // Fetch GL accounts with closing balance for selected FY
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId) return;
        dispatch(getGlAccountsClosing({ societyId, year: financialYear }));
    }, [dispatch, societyId, financialYear, refreshKey]);

    // API returns flat array directly in data
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const accounts = Array.isArray(glAccountsData) ? glAccountsData : [];

    const totals = useMemo(() => {
        let totalDebit = 0;
        let totalCredit = 0;
        accounts.forEach(acc => {
            totalDebit += parseFloat(acc.dr_amount) || 0;
            totalCredit += parseFloat(acc.cr_amount) || 0;
        });
        const difference = Math.abs(totalDebit - totalCredit);
        return { totalDebit, totalCredit, difference, isBalanced: difference < 0.01 };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts]);

    const formatCurrency = (amount) => parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const handleDownload = () => {
        if (accounts.length === 0) {
            toast.warn('No accounts to download');
            return;
        }
        const headers = ['Account Name', 'Account Code', 'Debit', 'Credit'];
        const rows = accounts.map(acc => [
            `"${acc.name}"`,
            acc.ac_code,
            parseFloat(acc.dr_amount) || 0,
            parseFloat(acc.cr_amount) || 0,
        ].join(','));
        rows.push(['', '', formatCurrency(totals.totalDebit), formatCurrency(totals.totalCredit)].join(','));
        const csv = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `closing_balance_${financialYear}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const getNextFY = () => {
        const parts = financialYear.split('-');
        return `${parseInt(parts[0]) + 1}-${parseInt(parts[1]) + 1}`;
    };

    const handleSave = (status) => {
        if (accounts.length === 0) {
            toast.warn('No closing balance data available');
            return;
        }
        dispatch(createAccountsClosing({
            societyId,
            year: financialYear,
            status,
            onSuccess: () => {
                dispatch(getGlAccountsClosing({ societyId, year: financialYear }));
            },
        }));
    };

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <div className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-gradient-to-br tw-from-orange-500 tw-to-red-600 tw-flex tw-items-center tw-justify-center tw-shadow-lg tw-shadow-orange-500/20">
                        <Icon icon="mdi:book-check" className="tw-text-xl tw-text-white" />
                    </div>
                    <div>
                        <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Closing Balance</h1>
                        <p className="tw-text-[11px] tw-text-gray-400">View calculated closing balances for the financial year</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {/* Financial Year */}
                    <SearchableDropdown
                        value={financialYear}
                        onChange={setFinancialYear}
                        options={financialYears}
                        placeholder="Financial Year"
                        clearable={false}
                        small
                    />

                    {/* Download */}
                    <button
                        onClick={handleDownload}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-red-600 tw-bg-white tw-border tw-border-red-200 tw-rounded-lg hover:tw-bg-red-50 tw-shadow-sm tw-transition-all"
                    >
                        <Icon icon="mdi:download-outline" className="tw-text-sm" />
                        Download
                    </button>

                    {/* Save as Draft */}
                    <button
                        onClick={() => handleSave('Draft')}
                        disabled={createLoading || accounts.length === 0}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-shadow-sm tw-transition-all disabled:tw-opacity-50"
                    >
                        {createLoading ? 'Saving...' : 'Save as Draft'}
                    </button>

                    {/* Submit */}
                    <button
                        onClick={() => handleSave('Submit')}
                        disabled={createLoading || accounts.length === 0}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-bg-red-600 tw-rounded-lg hover:tw-bg-red-700 tw-shadow-sm tw-transition-all disabled:tw-opacity-50"
                    >
                        {createLoading ? 'Submitting...' : 'Submit'}
                    </button>
                </div>
            </div>

            {/* Summary Card */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-p-4 tw-mb-4 tw-shadow-sm">
                <div className="tw-flex tw-items-center tw-gap-8">
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Debit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {formatCurrency(totals.totalDebit)}
                        </p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Total Credit</p>
                        <p className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mt-0.5 mb-0">
                            <span className="tw-text-sm tw-font-normal tw-text-gray-400 tw-mr-0.5">&#8377;</span>
                            {formatCurrency(totals.totalCredit)}
                        </p>
                    </div>
                    <div>
                        <p className="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium mb-0">Difference</p>
                        <div className="tw-flex tw-items-center tw-gap-2 tw-mt-0.5">
                            <p className={`tw-text-lg tw-font-bold ${totals.isBalanced ? 'tw-text-emerald-600' : 'tw-text-red-600'} mb-0`}>
                                <span className="tw-text-sm tw-font-normal tw-mr-0.5">&#8377;</span>
                                {formatCurrency(totals.difference)}
                            </p>
                            {totals.isBalanced && (
                                <Icon icon="mdi:check-circle" className="tw-text-emerald-500 tw-text-lg" />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Info Note */}
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl tw-p-4 tw-mb-4">
                <div className="tw-flex tw-items-start tw-gap-2">
                    <Icon icon="mdi:information-outline" className="tw-text-blue-500 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <p className="tw-text-xs tw-text-blue-700 tw-leading-relaxed mb-0">
                        <span className="tw-font-semibold">Note:</span> Closing balances are automatically calculated from approved journal entries within the selected financial year. Use <strong>Save as Draft</strong> or <strong>Submit</strong> to carry forward these balances as the opening balance for <strong>{getNextFY()}</strong>.
                    </p>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
                <table className="tw-w-full">
                    <thead>
                        <tr className="tw-border-b tw-border-gray-100 tw-bg-gray-50/50">
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[40%]">Account</th>
                            <th className="tw-text-left tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Account Code</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Debit (&#8377;)</th>
                            <th className="tw-text-right tw-px-5 tw-py-3 tw-text-[10px] tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-[20%]">Credit (&#8377;)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {glLoading ? (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-3">
                                        <div className="tw-w-10 tw-h-10 tw-rounded-full tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-animate-pulse">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-5 tw-w-5 tw-border-2 tw-border-blue-200 tw-border-t-blue-600"></div>
                                        </div>
                                        <span className="tw-text-gray-400 tw-text-xs">Calculating closing balances...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : accounts.length > 0 ? (
                            accounts.map(acc => {
                                const dr = parseFloat(acc.dr_amount) || 0;
                                const cr = parseFloat(acc.cr_amount) || 0;
                                return (
                                    <tr key={acc.account_id} className="tw-border-b tw-border-gray-50 hover:tw-bg-gray-50/50 tw-transition-colors">
                                        <td className="tw-px-5 tw-py-3">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <span className="tw-text-xs tw-font-medium tw-text-gray-700">{acc.name}</span>
                                                {acc.is_member_account && (
                                                    <span className="tw-text-[9px] tw-text-blue-600 tw-bg-blue-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium tw-border tw-border-blue-100">Member</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="tw-px-5 tw-py-3">
                                            <code className="tw-text-[11px] tw-text-gray-500 tw-bg-gray-50 tw-px-1.5 tw-py-0.5 tw-rounded tw-font-medium">{acc.ac_code}</code>
                                        </td>
                                        <td className="tw-px-5 tw-py-3 tw-text-right">
                                            <span className={`tw-text-xs tw-font-medium ${dr > 0 ? 'tw-text-gray-800' : 'tw-text-gray-300'}`}>
                                                {dr > 0 ? formatCurrency(dr) : '—'}
                                            </span>
                                        </td>
                                        <td className="tw-px-5 tw-py-3 tw-text-right">
                                            <span className={`tw-text-xs tw-font-medium ${cr > 0 ? 'tw-text-gray-800' : 'tw-text-gray-300'}`}>
                                                {cr > 0 ? formatCurrency(cr) : '—'}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="4" className="tw-text-center tw-py-20">
                                    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                        <div className="tw-w-14 tw-h-14 tw-rounded-2xl tw-bg-gray-50 tw-flex tw-items-center tw-justify-center">
                                            <Icon icon="mdi:book-check" className="tw-text-3xl tw-text-gray-300" />
                                        </div>
                                        <p className="tw-text-gray-400 tw-text-xs tw-font-medium">No closing balance data</p>
                                        <p className="tw-text-gray-300 tw-text-[10px]">No approved journal entries found for this financial year</p>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {accounts.length > 0 && (
                        <tfoot>
                            <tr className="tw-border-t-2 tw-border-gray-200 tw-bg-gray-50/80">
                                <td className="tw-px-5 tw-py-3 tw-text-xs tw-font-bold tw-text-gray-700" colSpan="2">Total</td>
                                <td className="tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-bold tw-text-emerald-600">
                                    &#8377; {formatCurrency(totals.totalDebit)}
                                </td>
                                <td className="tw-px-5 tw-py-3 tw-text-right tw-text-xs tw-font-bold tw-text-red-600">
                                    &#8377; {formatCurrency(totals.totalCredit)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
};

export default Closing;
